import { useEffect, useRef, useState } from 'react'
import { Image as KonvaImage, Layer, Stage } from 'react-konva'
import { VisualShape, VisualZoom } from 'app/frontend/types/custom/VisualPlan'
import { cn } from '@/lib/utils'
import { FALLBACK_ASPECT, zoomStageAttrs } from './constants'
import { useCanvasZoomPan } from './hooks/useCanvasZoomPan'
import { useEncounterImage } from './hooks/useEncounterImage'
import { useViewZoom } from './hooks/useViewZoom'
import AbilityWowheadOverlay from './panels/AbilityWowheadOverlay'
import EntityBadgeTooltipOverlay from './panels/EntityBadgeTooltipOverlay'
import { getEntityNumberStarts } from './shapes/entityNumbering'
import { ResolvedEntityBadge } from './shapes/entityAssignmentMatching'
import ShapeRenderer from './shapes/ShapeRenderer'
import ZoomOverviewThumbnail from './ZoomOverviewThumbnail'
import { EncounterAssignmentEntityOverride } from '../../../TeamEvent/components/eventActivityPlanOverrides'
import { ResolvedAssignmentParticipant } from '../../../TeamEvent/components/eventActivityTimelineResolver'

const isMacPlatform =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent)

const scrollHintText = isMacPlatform
  ? 'Use \u2318 + scroll to zoom'
  : 'Use Ctrl + scroll to zoom'

type Props = {
  encounterId: number | undefined
  imageIndex: number
  shapes: VisualShape[]
  zoom?: VisualZoom
  showZoomOverview?: boolean
  zoomable?: boolean
  entityAssignments?: Map<number, ResolvedEntityBadge>
  className?: string
  screenId?: string
  canEditEntities?: boolean
  canEditUnassignedSlots?: boolean
  selectedParticipants?: ResolvedAssignmentParticipant[]
  onEntityOverrideSelect?: (
    slotKey: string,
    override: EncounterAssignmentEntityOverride,
  ) => void
}

const VisualCanvas = ({
  encounterId,
  imageIndex,
  shapes,
  zoom,
  showZoomOverview = true,
  zoomable = true,
  entityAssignments,
  className,
  screenId,
  canEditEntities = false,
  canEditUnassignedSlots = true,
  selectedParticipants,
  onEntityOverrideSelect,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [viewZoom, setViewZoom] = useViewZoom(zoom)

  const image = useEncounterImage(encounterId, imageIndex)
  const lastAspectRef = useRef(FALLBACK_ASPECT)
  if (image) lastAspectRef.current = image.height / image.width
  const aspect = image ? image.height / image.width : lastAspectRef.current
  const height = width * aspect
  const zoomScale = viewZoom && viewZoom.scale > 1 ? viewZoom.scale : 1
  const entityNumberStarts = getEntityNumberStarts(shapes)
  const [showScrollHint, setShowScrollHint] = useState(false)
  const scrollHintTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { onWheelZoom } = useCanvasZoomPan({
    width,
    height,
    zoom: viewZoom,
    onZoomChange: setViewZoom,
  })

  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    const observer = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(
    () => () => {
      if (scrollHintTimeout.current) clearTimeout(scrollHintTimeout.current)
    },
    [],
  )

  const triggerScrollHint = () => {
    setShowScrollHint(true)
    if (scrollHintTimeout.current) clearTimeout(scrollHintTimeout.current)
    scrollHintTimeout.current = setTimeout(() => setShowScrollHint(false), 1500)
  }

  useEffect(() => {
    const element = containerRef.current
    if (!element || !zoomable) return
    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) {
        triggerScrollHint()
        return
      }
      event.preventDefault()
      const rect = element.getBoundingClientRect()
      onWheelZoom(
        event.clientX - rect.left,
        event.clientY - rect.top,
        event.deltaY,
      )
    }
    element.addEventListener('wheel', handleWheel, { passive: false })
    return () => element.removeEventListener('wheel', handleWheel)
  }, [zoomable, onWheelZoom])

  return (
    <div
      ref={containerRef}
      className={cn('relative', className)}
      style={{ aspectRatio: `1 / ${aspect}` }}
    >
      {width > 0 ? (
        <>
          <Stage
            width={width}
            height={height}
            listening={true}
            {...zoomStageAttrs(viewZoom, width, height)}
          >
            <Layer listening={false}>
              {image ? (
                <KonvaImage
                  image={image}
                  width={width}
                  height={height}
                  listening={false}
                />
              ) : null}
              {shapes.map((shape) => (
                <ShapeRenderer
                  key={shape.id}
                  shape={shape}
                  width={width}
                  height={height}
                  zoomScale={zoomScale}
                  entityStartNumber={entityNumberStarts.get(shape.id) ?? 1}
                  entityAssignments={entityAssignments}
                  encounterId={encounterId}
                />
              ))}
            </Layer>
          </Stage>
          <EntityBadgeTooltipOverlay
            shapes={shapes}
            canvasWidth={width}
            canvasHeight={height}
            zoom={viewZoom}
            entityAssignments={entityAssignments}
            screenId={screenId}
            canEdit={canEditEntities}
            canEditUnassignedSlots={canEditUnassignedSlots}
            selectedParticipants={selectedParticipants}
            onOverrideSelect={onEntityOverrideSelect}
          />
          <AbilityWowheadOverlay
            shapes={shapes}
            canvasWidth={width}
            canvasHeight={height}
            zoom={viewZoom}
          />
        </>
      ) : null}
      {zoomable && showZoomOverview && viewZoom && viewZoom.scale > 1 ? (
        <ZoomOverviewThumbnail
          image={image}
          aspect={aspect}
          zoom={viewZoom}
          onZoomChange={setViewZoom}
        />
      ) : null}
      {showScrollHint ? (
        <div className='pointer-events-none absolute bottom-2 left-2 rounded-md border border-border bg-background/90 px-2 py-1 text-xs text-muted-foreground shadow-sm'>
          {scrollHintText}
        </div>
      ) : null}
    </div>
  )
}

export default VisualCanvas
